import { useMutation } from '@apollo/client/react';
import { getLoggedInAs } from '@realadvisor/auth-client';
import { encodeGlobalId } from '@realadvisor/transform/global-id';

import { SEND_SLACK_NOTIFICATION } from '@modules/_shared/components/profilePageLayoutQueries';
import { isProduction } from '@shared/config';

// "I'm interested" in the paid profile: a post in the country channel, where the
// demo booking flows also post. A booking post following it means the agent
// booked; otherwise the sales team calls. Contact details stay behind the CRM
// link: the post carries only the name.
const COUNTRY_CHANNELS: Partial<Record<string, string>> = {
  PL: '#poland',
  PT: '#portugal',
};

type PaidProfileInterest = {
  userId: string;
  countryCode: string | null | undefined;
  language: string;
  page: 'claim' | 'takedown';
  isAgency: boolean;
  name: string;
  agencyName: string | null;
};

// Names and the agency are user-controlled: keep them from forming Slack links
// or mentions.
const escapeSlackText = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const useSendPaidProfileInterest = () => {
  const [sendSlack] = useMutation(SEND_SLACK_NOTIFICATION);

  return (interest: PaidProfileInterest) => {
    // Staff under "Login as" carry the agent's session: their clicks must not
    // reach the sales team (as PostHog and Customer.io skip them).
    if (getLoggedInAs() != null) {
      return;
    }
    const channel = isProduction
      ? COUNTRY_CHANNELS[interest.countryCode ?? '']
      : '#test-n8n-notification';
    if (channel == null) {
      return;
    }
    const name = escapeSlackText(interest.name);
    const agency =
      interest.agencyName != null ? escapeSlackText(interest.agencyName) : null;
    const who = interest.isAgency
      ? `${name} for the agency ${agency ?? '?'}`
      : agency != null
      ? `${name} (${agency})`
      : name;
    const crmUrl = `${window.location.origin}/${
      interest.language
    }/v1/users/${encodeURIComponent(
      encodeGlobalId('User', interest.userId),
    )}/overview`;
    const text = [
      `:star: *Paid profile interest* (${interest.page} page)`,
      who,
      `CRM: ${crmUrl}`,
      '_The page offered a demo booking: if they book, the booking post follows here. Otherwise, give them a call._',
    ].join('\n');

    // Fire and forget: the page already shows the next step (book a demo).
    void sendSlack({ variables: { channel, text } }).catch(() => undefined);
  };
};
